import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import DataInputComponent from './DataInputComponent';
import DragDropComponent from './DragDropComponent';
import SpreadsheetTableComponent from './SpreadsheetTableComponent';

import { makeColumns, makeRows } from './utils';

import { XLSX_ERROR } from '../Shared/constants/errors';

const SpreadsheetContainer = (props) => {
  const {
    onImport,
    onImportTitle,
    onFileUpload,
    showImportedData,
    isEditable,
    columnsKeyNames,
  } = props;

  const [spreadsheetData, setSpreadsheetData] = useState({
    entries: [],
    columns: [],
    file: null,
    cols: [],
    update: false,
  });

  const renderEditable = (cellInfo) => {
    const reHTMLTags = /<[^>]*>/gm;
    const reHTMLEntities = /&[^;]*;/gm;
    if (!isEditable) {
      return (
        <div>{spreadsheetData.entries[cellInfo.index][cellInfo.column.id]}</div>
      );
    }
    return (
      <div
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => {
          const data = [...spreadsheetData.entries];
          data[cellInfo.index][cellInfo.column.id] = String(e.target.innerHTML)
            .replace(reHTMLTags, '')
            .replace(reHTMLEntities, '')
            .trim();
          setSpreadsheetData((spreadsheetData) => ({
            ...spreadsheetData,
            entries: data,
          }));
        }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: spreadsheetData.entries[cellInfo.index][cellInfo.column.id],
        }}
      />
    );
  };

  useEffect(() => {
    if (spreadsheetData.cols.length && spreadsheetData.update) {
      setSpreadsheetData((spreadsheetData) => ({
        ...spreadsheetData,
        columns: makeColumns(
          spreadsheetData.cols,
          columnsKeyNames,
          renderEditable,
        ),
        update: false,
      }));
    }
  }, [spreadsheetData.entries]);

  const handleFile = async (file) => {
    try {
      const { default: XLSX } = await import('xlsx');
      const reader = new FileReader();
      reader.onload = (e) => {
        // Parse data and create a xlsx workbook
        const dataArrayBuffer = e.target.result;
        const workbook = XLSX.read(dataArrayBuffer, { type: 'array' });

        // Get the first worksheet name
        const workSheetName = workbook.SheetNames[0];

        // Get worksheet by its name
        const worksheet = workbook.Sheets[workSheetName];

        // Create an array of arrays (rows) of JS values from a worksheet
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // The first row is considered to be a header
        const header = rows.shift();
        const cols = header?.map((columnName, i) => ({
          name: columnName,
          key: i,
        }));

        // Format the rest of the arrays based on the required data structure
        const entries = makeRows(columnsKeyNames, rows);

        setSpreadsheetData({
          columns: [],
          entries,
          file,
          cols,
          update: true,
        });
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      toast.error(XLSX_ERROR);
    }
  };

  const showTable =
    showImportedData &&
    spreadsheetData.entries.length &&
    spreadsheetData.columns.length;

  return (
    <DragDropComponent handleFile={handleFile}>
      <div>
        <DataInputComponent
          handleFile={handleFile}
          data={spreadsheetData.entries}
          onImport={onImport}
          onImportTitle={onImportTitle}
          file={spreadsheetData.file}
          onFileUpload={onFileUpload}
        />
      </div>
      {showTable ? (
        <Box mt="4rem">
          <SpreadsheetTableComponent
            entries={spreadsheetData.entries}
            columns={spreadsheetData.columns}
          />
        </Box>
      ) : null}
    </DragDropComponent>
  );
};

SpreadsheetContainer.defaultProps = {
  isEditable: true,
  onImport: () => {},
  onFileUpload: null,
  onImportTitle: '',
  showImportedData: true,
};

SpreadsheetContainer.propTypes = {
  isEditable: PropTypes.bool,
  onImport: PropTypes.func,
  onFileUpload: PropTypes.func,
  onImportTitle: PropTypes.string,
  showImportedData: PropTypes.bool,
  columnsKeyNames: PropTypes.instanceOf(Array),
};

export default SpreadsheetContainer;
